import { useEffect, useState } from "react";
import axios from "axios";
import { Landmark } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { LockedPanel } from "@/components/entitlements/LockedPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => (n == null ? ", " : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${Math.round(n / 1e3)}K`);
const pct = (n) => (n == null ? ", " : `${n}%`);

const MIX = [
  { key: "pacSharePct", label: "PACs", color: "#F7670F" },
  { key: "individualSharePct", label: "Individuals", color: "#38BDF8" },
  { key: "transfersSharePct", label: "Transfers", color: "#A78BFA" },
];

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] font-mono uppercase tracking-wider text-white/40">{label}</p>
    <p className="text-lg font-black font-mono tabular-nums text-white">{value}</p>
  </div>
);

const shortName = (n) => (n || "").replace(/\s*\([^)]*$/, "").replace(/\s*\(.*?\)\s*/g, " ").replace(/political action committee.*$/i, "PAC").replace(/\s+/g, " ").trim();

const NameList = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1.5">{title}</p>
    {items?.length ? (
      <ul className="space-y-1">
        {items.slice(0, 5).map((x, i) => (
          <li key={i} className="flex justify-between gap-2 text-xs">
            <span className="text-white/80 truncate" title={x.name}>{shortName(x.name)}</span>
            <span className="font-mono tabular-nums text-white/50 shrink-0">{money(x.total)}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-xs text-white/40">None itemized this cycle.</p>
    )}
  </div>
);

export const MoneyPanel = ({ legislatorId }) => {
  const { featureMode } = useAuth();
  const locked = featureMode("campaign_finance") === "none";
  const [fin, setFin] = useState(null);

  useEffect(() => {
    if (locked) return undefined;
    let alive = true;
    let timer = null;
    const poll = (attempt) =>
      axios.get(`${API}/legislators/${legislatorId}/finance`, { validateStatus: (s) => s === 200 || s === 202 })
        .then((r) => {
          if (!alive) return;
          if (r.status === 202) { if (attempt < 8) timer = setTimeout(() => poll(attempt + 1), 8000); else setFin({ available: false }); return; }
          setFin(r.data);
        })
        .catch(() => alive && setFin({ available: false }));
    poll(0);
    return () => { alive = false; clearTimeout(timer); };
  }, [legislatorId, locked]);

  if (locked) {
    return <LockedPanel light feature="campaign_finance" tier="pro" title="Campaign Money"
      blurb="Donor mix, in-state versus out-of-state money, top PACs and donor employers from FEC filings." />;
  }

  const other = fin?.receipts ? Math.max(0, 100 - MIX.reduce((a, m) => a + (fin[m.key] || 0), 0)) : 0;
  return (
    <Card className="p-6 rounded-xl border border-white/10" style={{ background: "var(--whip-surface, #131318)" }} data-testid="money-panel">
      <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
        <Landmark className="w-5 h-5 text-orange-400" /> Campaign Money
      </h2>
      {fin === null ? (
        <p className="text-sm text-white/50 animate-pulse" data-testid="money-panel-loading">Pulling FEC filings…</p>
      ) : !fin.available && !fin.receipts ? (
        <p className="text-sm text-white/50" data-testid="money-panel-unavailable">No FEC filings matched this member for the current cycle.</p>
      ) : (
        <>
          <p className="text-xs text-white/45 mb-4">{fin.cycle} cycle{fin.committee ? ` · ${fin.committee}` : ""}</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Stat label="Total Raised" value={money(fin.receipts)} testId="money-receipts" />
            <Stat label="Cash On Hand" value={money(fin.cashOnHand)} testId="money-cash" />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1.5">Donor Mix</p>
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/10" data-testid="money-mix-bar">
            {MIX.map((m) => (fin[m.key] > 0 ? <div key={m.key} style={{ width: `${fin[m.key]}%`, background: m.color }} title={`${m.label} ${fin[m.key]}%`} /> : null))}
            {other > 0 && <div style={{ width: `${other}%`, background: "rgba(255,255,255,0.18)" }} title={`Other ${other}%`} />}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 mb-4 text-xs">
            {MIX.map((m) => (
              <span key={m.key} className="flex items-center gap-1.5 text-white/70" data-testid={`money-mix-${m.key}`}>
                <span className="inline-block w-2 h-2 rounded-sm" style={{ background: m.color }} />{m.label} <span className="font-mono text-white/90">{pct(fin[m.key])}</span>
              </span>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4 pt-3 border-t border-white/10">
            <Stat label="Small-Dollar (<$200)" value={pct(fin.smallDollarSharePct)} testId="money-small-dollar" />
            <Stat label="In-State Donors" value={pct(fin.inStateSharePct)} testId="money-in-state" />
          </div>
          {fin.topDonorStates?.length > 0 && (
            <p className="text-xs text-white/60 mb-4" data-testid="money-top-states">
              Top donor states: <span className="font-mono text-white/85">{fin.topDonorStates.join(" · ")}</span>
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2 pt-3 border-t border-white/10">
            <NameList title="Top PACs" items={fin.topPacs} testId="money-top-pacs" />
            <NameList title="Top Donor Employers" items={fin.topEmployers} testId="money-top-employers" />
          </div>
          <p className="text-[11px] text-white/35 mt-4" data-testid="money-source">
            Source: Federal Election Commission{fin.coverageEnd ? `, filings through ${new Date(fin.coverageEnd).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}` : ""}.
            Small-dollar and in-state shares are of individual contributions.
          </p>
        </>
      )}
    </Card>
  );
};
